import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { monthNames } from '../../../../../../core/commom/monthsNames';
import { Icon } from '../../../../../../core/components/atoms/Icon';

interface IMoveContentCalendar {
  value: Date | null;
  onSelectDate: (date: Date) => void;
}

const weekDays = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

const MoveContentCalendar: React.FC<IMoveContentCalendar> = ({
  value,
  onSelectDate,
}) => {
  const [visibleMonth, setVisibleMonth] = useState(
    startOfMonth(value ?? new Date())
  );
  const today = startOfDay(new Date());

  useEffect(() => {
    if (value) {
      setVisibleMonth(startOfMonth(value));
    }
  }, [value]);

  const visibleMonthDays = useMemo(() => {
    const interval = {
      start: startOfWeek(startOfMonth(visibleMonth), { weekStartsOn: 0 }),
      end: endOfWeek(endOfMonth(visibleMonth), { weekStartsOn: 0 }),
    };

    return eachDayOfInterval(interval);
  }, [visibleMonth]);

  return (
    <div className='rounded-md border-hairline border-mono-color-light bg-mono-color-white px-3 py-2'>
      <div className='mb-2 flex items-center justify-between'>
        <button
          type='button'
          onClick={() => setVisibleMonth((prev) => subMonths(prev, 1))}
          className='flex h-6 w-6 cursor-pointer items-center justify-center rounded-sm text-mono-color-darker hover:bg-mono-color-lightest'
        >
          <Icon Icon='chevron_left' type='material' className='text-sm' />
        </button>
        <span className='text-md font-bold text-mono-color-black'>
          {monthNames[visibleMonth.getMonth()]} {visibleMonth.getFullYear()}
        </span>
        <button
          type='button'
          onClick={() => setVisibleMonth((prev) => addMonths(prev, 1))}
          className='flex h-6 w-6 cursor-pointer items-center justify-center rounded-sm text-mono-color-darker hover:bg-mono-color-lightest'
        >
          <Icon Icon='chevron_right' type='material' className='text-sm' />
        </button>
      </div>

      <div className='mb-1 grid grid-cols-7'>
        {weekDays.map((day) => (
          <span
            key={day}
            className='text-center text-[11px] font-bold text-mono-color-medium'
          >
            {day}
          </span>
        ))}
      </div>

      <div className='grid grid-cols-7 gap-y-[2px]'>
        {visibleMonthDays.map((day) => {
          const isCurrentMonthDay = isSameMonth(day, visibleMonth);
          const isPastDate = isBefore(day, today);
          const isSelectedDate = value ? isSameDay(day, value) : false;
          const isDisabled = !isCurrentMonthDay || isPastDate;

          return (
            <button
              key={day.toISOString()}
              type='button'
              disabled={isDisabled}
              onClick={() => onSelectDate(day)}
              className={`
                mx-auto flex h-8 w-8 items-center justify-center rounded-sm text-sm
                ${isSelectedDate ? 'bg-brand-color-primary-pure text-mono-color-default' : ''}
                ${!isSelectedDate && !isDisabled ? 'cursor-pointer text-mono-color-darker hover:bg-mono-color-lightest' : ''}
                ${isDisabled && isCurrentMonthDay ? 'cursor-not-allowed text-mono-color-light' : ''}
                ${!isCurrentMonthDay ? 'cursor-not-allowed text-mono-color-light' : ''}
              `}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MoveContentCalendar;
